// Per-contact detail page with the unified ContactTimeline. Pulls
// the contact row + its associated user/listing so the header can
// link back to either record. Send Direct Email composer is mounted
// here too — same one used on the user-detail page.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import client from '../../../lib/feathers';
import ContactTimeline from '../../../components/ContactTimeline';
import EmailEditor from '../../../components/EmailEditor';
import TemplatePicker from '../../../components/TemplatePicker';

const STATUS_PILL = {
  active: 'bg-green-100 text-green-800',
  unsubscribed: 'bg-gray-200 text-gray-700',
  bounced: 'bg-red-100 text-red-800',
};

export default function ContactDetail() {
  const { contactId } = useParams();
  const [contact, setContact] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCompose, setShowCompose] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [variables, setVariables] = useState([]);

  useEffect(() => {
    let cancelled = false;
    client.service('admin/email-variable-catalog').find({})
      .then((r) => { if (!cancelled) setVariables(r.data || []); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    client.service('admin/outreach-contact').get(contactId)
      .then((row) => { if (!cancelled) setContact(row); })
      .catch((err) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, [contactId]);

  const sendDirect = async () => {
    setSending(true);
    setSendError(null);
    try {
      await client.service('admin/campaign-direct-send').create({
        recipientContactId: contactId,
        subject: composeSubject,
        bodyHtml: composeBody,
      });
      setShowCompose(false);
      setComposeSubject('');
      setComposeBody('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setSendError(err.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (loadError) {
    return <div className="p-6"><p className="text-red-600">{loadError}</p></div>;
  }
  if (!contact) {
    return <div className="p-6"><p className="text-gray-500">Loading…</p></div>;
  }

  const pillCls = STATUS_PILL[contact.status] || 'bg-yellow-100 text-yellow-800';

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{contact.name || contact.email}</h2>
            <p className="text-sm text-gray-600 mt-1">{contact.email}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${pillCls}`}>
                {contact.status}
              </span>
              {contact.userId && (
                <Link to={`/admin/users/${contact.userId}`} className="text-xs text-blue-700 hover:underline">
                  Linked user →
                </Link>
              )}
              {contact.listingId && (
                <Link to={`/admin/listings/${contact.listingId}`} className="text-xs text-blue-700 hover:underline">
                  Linked listing →
                </Link>
              )}
              <span className="text-xs text-gray-500">source: {contact.source}</span>
            </div>
          </div>
          <button
            onClick={() => { setShowCompose(true); setComposeSubject(''); setComposeBody(''); setSendError(null); }}
            disabled={contact.status !== 'active'}
            className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send Direct Email
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Timeline</h3>
          <p className="text-sm text-gray-500 mt-1">Every message, engagement event, journey enrollment, and tier change</p>
        </div>
        <div className="px-6 py-5">
          <ContactTimeline contactId={contactId} refreshKey={refreshKey} />
        </div>
      </div>

      {showCompose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Send Direct Email</h3>
              <p className="text-sm text-gray-600 mt-1">To: {contact.email}</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              {sendError && <p className="text-sm text-red-600">{sendError}</p>}
              <TemplatePicker onPick={(tpl) => {
                if (!tpl) return;
                setComposeSubject(tpl.subject || '');
                setComposeBody(tpl.bodyHtml || '');
              }}
              />
              <div>
                <label htmlFor="contactSubject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  id="contactSubject"
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  maxLength={200}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                <EmailEditor
                  value={composeBody}
                  onChange={setComposeBody}
                  variables={variables}
                  placeholder="Write your message…"
                  minHeight="240px"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-appends an unsubscribe footer. Sent via Postmark outbound stream.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
              <button onClick={() => setShowCompose(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
              <button
                onClick={sendDirect}
                disabled={sending || !composeSubject.trim() || !composeBody.trim()}
                className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
